import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { startOfDay, subMonths } from 'date-fns';
import { compact, isEqual, keyBy } from 'lodash-es';
import { DatePicker, FilterPicker, RadioButtonWithLabel } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { dateRangePickerTextsPath } from '~/components/TableFilters/DateRangePicker/DateRangePicker';
import { DateRange, SearchFilterType } from '~/components/TableFilters/types';
import { getRecurrenceFilterOptions } from '~/constants';
export { RecurrenceFilterValue } from '~/constants';
import { useIoContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { relationshipSummaryModel, timeModel } from '~/models';
import { formatNumericUSDate } from '~/utils';
export const agreementFilterTextsPath = 'agreement';
export var FilterType = /*#__PURE__*/ function(FilterType) {
    FilterType["AgreementStatus"] = "agreementStatus";
    FilterType["EffectiveDate"] = "effectiveDate";
    FilterType["ApprovalDate"] = "approvalDate";
    FilterType["LastBillingDate"] = "lastBillingDate";
    FilterType["PaymentMethod"] = "paymentMethod";
    FilterType["HasCredit"] = "hasCredit";
    FilterType["Completion"] = "completion";
    FilterType["BillingTrigger"] = "billingTrigger";
    FilterType["Recurrence"] = "recurrence";
    FilterType["Automatic"] = "automatic";
    FilterType["ServiceNames"] = "serviceNames";
    FilterType["ServicePriceIncreaseState"] = "servicePriceIncreaseState";
    FilterType["HasAmendments"] = "hasAmendments";
    FilterType["AmendmentsStatus"] = "amendmentsStatus";
    return FilterType;
}({});
export var PriceIncreaseFilterValue = /*#__PURE__*/ function(PriceIncreaseFilterValue) {
    PriceIncreaseFilterValue["None"] = "none";
    PriceIncreaseFilterValue["Automatic"] = "automatic";
    return PriceIncreaseFilterValue;
}({});
export const defaultAgreementStatusFilter = [
    relationshipSummaryModel.AgreementStatus.Active,
    relationshipSummaryModel.AgreementStatus.Exhausted
];
export const agreementStudioAgreementsArgs = {
    filters: {
        agreementStatus: defaultAgreementStatusFilter
    }
};
export function getTableFilterLabels(filterLabels, aiFilterLabels, aiVariant) {
    return {
        ["agreementStatus"]: filterLabels.agreementStatus(),
        ["effectiveDate"]: filterLabels.effectiveDate(),
        ["approvalDate"]: filterLabels.approvalDate(),
        ["lastBillingDate"]: filterLabels.lastBillingDate(),
        ["paymentMethod"]: filterLabels.paymentMethod(),
        ["hasCredit"]: aiVariant ? aiFilterLabels.hasCredit() : filterLabels.hasCredit(),
        ["completion"]: filterLabels.completion(),
        ["recurrence"]: filterLabels.recurrence(),
        ["automatic"]: filterLabels.automatic(),
        ["billingTrigger"]: filterLabels.billingTrigger(),
        ["serviceNames"]: filterLabels.serviceNames(),
        ["servicePriceIncreaseState"]: filterLabels.servicePriceIncreaseState(),
        ["hasAmendments"]: filterLabels.hasAmendments(),
        ["amendmentsStatus"]: filterLabels.amendmentsStatus()
    };
}
export function getTableFilterGroups(filterGroupLabels, amendmentDraftEnabled, approvalDateEnabled) {
    return [
        {
            label: filterGroupLabels.agreements(),
            filters: compact([
                "agreementStatus",
                "effectiveDate",
                approvalDateEnabled && "approvalDate",
                "lastBillingDate",
                "paymentMethod",
                "hasCredit",
                "servicePriceIncreaseState",
                amendmentDraftEnabled ? "amendmentsStatus" : "hasAmendments"
            ])
        },
        {
            label: filterGroupLabels.services(),
            filters: [
                "serviceNames",
                "completion",
                "billingTrigger",
                "recurrence",
                "automatic"
            ]
        }
    ];
}
export function deserializeFiltersFromURL(urlParams, approvalDateEnabled) {
    return {
        ["agreementStatus"]: deserializeFilter("agreementStatus", urlParams.agreementStatus),
        ["effectiveDate"]: deserializeFilter("effectiveDate", urlParams.effectiveDate),
        ["approvalDate"]: approvalDateEnabled ? deserializeFilter("approvalDate", urlParams.approvalDate) : emptyApprovalDate(),
        ["lastBillingDate"]: deserializeFilter("lastBillingDate", urlParams.lastBillingDate),
        ["paymentMethod"]: deserializeFilter("paymentMethod", urlParams.paymentMethod),
        ["completion"]: deserializeFilter("completion", urlParams.completion),
        ["recurrence"]: deserializeFilter("recurrence", urlParams.recurrence),
        ["hasCredit"]: deserializeFilter("hasCredit", urlParams.hasCredit),
        ["automatic"]: deserializeFilter("automatic", urlParams.automatic),
        ["billingTrigger"]: deserializeFilter("billingTrigger", urlParams.billingTrigger),
        ["serviceNames"]: deserializeFilter("serviceNames", urlParams.serviceNames),
        ["servicePriceIncreaseState"]: deserializeFilter("servicePriceIncreaseState", urlParams.servicePriceIncreaseState),
        ["hasAmendments"]: deserializeFilter("hasAmendments", urlParams.hasAmendments),
        ["amendmentsStatus"]: deserializeFilter("amendmentsStatus", urlParams.amendmentsStatus)
    };
    function deserializeFilter(type, value) {
        const { deserialize } = filtersConfig[type];
        if (typeof value !== 'string') return value;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return deserialize ? deserialize(value) : value;
    }
}
function getAgreementStatusFilterOptions(labels) {
    return [
        {
            value: relationshipSummaryModel.AgreementStatus.Active,
            label: labels.active()
        },
        {
            value: relationshipSummaryModel.AgreementStatus.Exhausted,
            label: labels.completed()
        },
        {
            value: relationshipSummaryModel.AgreementStatus.Terminated,
            label: labels.terminated()
        }
    ];
}
export var HasAmendmentsFilterValue = /*#__PURE__*/ function(HasAmendmentsFilterValue) {
    HasAmendmentsFilterValue["True"] = "true";
    HasAmendmentsFilterValue["False"] = "false";
    return HasAmendmentsFilterValue;
}({});
export var AmendmentsStatusFilterValue = /*#__PURE__*/ function(AmendmentsStatusFilterValue) {
    AmendmentsStatusFilterValue["AmendmentDraft"] = "amendmentDraft";
    AmendmentsStatusFilterValue["AwaitingApproval"] = "awaitingApproval";
    AmendmentsStatusFilterValue["NoChanges"] = "noChanges";
    return AmendmentsStatusFilterValue;
}({});
function getHasAmendmentsFilterOptions(labels) {
    return [
        {
            value: "true",
            label: labels.yes()
        },
        {
            value: "false",
            label: labels.no()
        }
    ];
}
function getAmendmentsStatusFilterOptions(labels) {
    let includeUnappliedOption = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : true;
    return compact([
        includeUnappliedOption && {
            value: "amendmentDraft",
            label: labels.amendmentDraft()
        },
        {
            value: "awaitingApproval",
            label: labels.awaitingApproval()
        },
        {
            value: "noChanges",
            label: labels.noChanges()
        }
    ]);
}
export const GetEffectiveDateFilterTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            EffectiveDateFilterTID: {
                Root: 'effective-date-filter',
                All: 'effective-date-filter-all',
                Range: 'effective-date-filter-range',
                DatePicker: 'effective-date-filter'
            }
        }
    });
export function EffectiveDateFilter(props) {
    const { EffectiveDateFilterTID } = useMemo(()=>GetEffectiveDateFilterTID(), []);
    const { value, startOpen, onChange, onRemove, onInputBlur, onInputFocus } = props;
    const typedValue = value !== null && value !== void 0 ? value : {};
    const { getNow } = useIoContext();
    const { t: agreementFilterTexts } = useLocaleKeys(agreementFilterTextsPath);
    const effectiveDateLabel = agreementFilterTexts.filters.labels.effectiveDate();
    const hasDatesFromProps = !!(typedValue.fromDate && typedValue.toDate);
    const [selectingRange, setSelectingRange] = useState(hasDatesFromProps);
    const [fromDate, setFromDate] = useState(getInitialFromDate);
    const [toDate, setToDate] = useState(getInitialToDate);
    useEffect(()=>{
        typedValue.fromDate && setFromDate(typedValue.fromDate);
    }, [
        typedValue.fromDate
    ]);
    useEffect(()=>{
        typedValue.toDate && setToDate(typedValue.toDate);
    }, [
        typedValue.toDate
    ]);
    const label = useMemo(()=>{
        if (typedValue.fromDate && typedValue.toDate) {
            return "".concat(effectiveDateLabel, " (").concat(timeModel.formatDate(typedValue.fromDate), "-").concat(timeModel.formatDate(typedValue.toDate), ")");
        }
        return effectiveDateLabel;
    }, [
        effectiveDateLabel,
        typedValue.fromDate,
        typedValue.toDate
    ]);
    function onChangeIfDifferentValue(newValue) {
        if (!isEqual(newValue, typedValue)) {
            onChange(newValue);
        }
    }
    function getInitialFromDate() {
        return typedValue.fromDate || subMonths(getNow(), 1);
    }
    function getInitialToDate() {
        return typedValue.toDate || getNow();
    }
    return /*#__PURE__*/ _jsxs(FilterPicker, {
        removable: true,
        startOpen: startOpen,
        testId: EffectiveDateFilterTID.Root(),
        disableApply: selectingRange && fromDate && toDate && fromDate.getTime() > toDate.getTime(),
        label: label,
        title: effectiveDateLabel,
        highlightTrigger: !!(typedValue.fromDate && typedValue.toDate),
        onRemove: onRemove,
        onApply: ()=>onChangeIfDifferentValue(selectingRange ? {
                fromDate,
                toDate
            } : {}),
        onClose: (opts)=>{
            if (opts === null || opts === void 0 ? void 0 : opts.revert) {
                setSelectingRange(hasDatesFromProps);
                setFromDate(getInitialFromDate());
                setToDate(getInitialToDate());
            }
        },
        children: [
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'effective_date_all'
                },
                label: agreementFilterTexts.filters.dateRange.selectAll(),
                testId: EffectiveDateFilterTID.All(),
                checked: !selectingRange,
                onChange: ()=>setSelectingRange(false)
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'effective_date_range'
                },
                label: agreementFilterTexts.filters.dateRange.specificRange(),
                testId: EffectiveDateFilterTID.Range(),
                checked: selectingRange,
                onChange: ()=>{
                    setSelectingRange(true);
                    const now = startOfDay(getNow());
                    if (!fromDate) {
                        setFromDate(now);
                    }
                    if (!toDate) {
                        setToDate(now);
                    }
                }
            }),
            selectingRange && fromDate && toDate && /*#__PURE__*/ _jsx(DateRangePicker, {
                testId: EffectiveDateFilterTID.DatePicker(),
                fromDate: fromDate,
                toDate: toDate,
                onChange: (data)=>{
                    setFromDate(data.fromDate);
                    setToDate(data.toDate);
                },
                onInputFocus: onInputFocus,
                onInputBlur: onInputBlur
            })
        ]
    });
}
export var LastBillingDateType = /*#__PURE__*/ function(LastBillingDateType) {
    LastBillingDateType["None"] = "none";
    LastBillingDateType["Never"] = "never";
    LastBillingDateType["Ever"] = "ever";
    LastBillingDateType["Range"] = "range";
    return LastBillingDateType;
}({});
function getLastBillingDateOptionLabel(type, labels) {
    switch(type){
        case "ever":
            return labels.everBilled();
        case "never":
            return labels.neverBilled();
        case "range":
            return labels.specificRange();
        case "none":
            return '';
    }
}
export const GetLastBillingDateFilterTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            LastBillingDateFilterTID: {
                Root: 'last-billing-date-filter',
                All: 'last-billing-date-filter-all',
                Ever: 'last-billing-date-filter-ever',
                Never: 'last-billing-date-filter-never',
                Range: 'last-billing-date-filter-range',
                DatePicker: 'last-billing-date-filter'
            }
        }
    });
export function LastBillingDateFilter(props) {
    const { LastBillingDateFilterTID } = useMemo(()=>GetLastBillingDateFilterTID(), []);
    const { value, startOpen, onChange, onRemove, onInputBlur, onInputFocus } = props;
    const typedValue = value !== null && value !== void 0 ? value : {
        type: "none"
    };
    const { getNow } = useIoContext();
    const { t: agreementFilterTexts } = useLocaleKeys(agreementFilterTextsPath);
    const lastBillingDateLabel = agreementFilterTexts.filters.labels.lastBillingDate();
    const [type, setType] = useState(typedValue.type);
    const [fromDate, setFromDate] = useState(getInitialFromDate);
    const [toDate, setToDate] = useState(getInitialToDate);
    const disableApply = fromDate && toDate && fromDate.getTime() > toDate.getTime();
    useEffect(()=>{
        setType(typedValue.type);
    }, [
        typedValue.type
    ]);
    useEffect(()=>{
        typedValue.fromDate && setFromDate(typedValue.fromDate);
    }, [
        typedValue.fromDate
    ]);
    useEffect(()=>{
        typedValue.toDate && setToDate(typedValue.toDate);
    }, [
        typedValue.toDate
    ]);
    const label = useMemo(()=>{
        switch(typedValue.type){
            case "ever":
            case "never":
                return "".concat(lastBillingDateLabel, " (").concat(getLastBillingDateOptionLabel(typedValue.type, agreementFilterTexts.filters.lastBillingDateOptions), ")");
            case "range":
                return "".concat(lastBillingDateLabel, " (").concat(timeModel.formatDate(typedValue.fromDate), "-").concat(timeModel.formatDate(typedValue.toDate), ")");
            case "none":
                return lastBillingDateLabel;
        }
    }, [
        typedValue
    ]);
    function onChangeIfDifferentValue(newValue) {
        if (!isEqual(newValue, typedValue)) {
            onChange(newValue);
        }
    }
    function getInitialFromDate() {
        return typedValue.fromDate || subMonths(getNow(), 1);
    }
    function getInitialToDate() {
        return typedValue.toDate || getNow();
    }
    return /*#__PURE__*/ _jsxs(FilterPicker, {
        removable: true,
        startOpen: startOpen,
        testId: LastBillingDateFilterTID.Root(),
        disableApply: disableApply,
        label: label,
        title: lastBillingDateLabel,
        highlightTrigger: typedValue.type !== "none",
        onRemove: onRemove,
        onApply: ()=>{
            if (type === "range") {
                fromDate && toDate && onChangeIfDifferentValue({
                    type,
                    fromDate,
                    toDate
                });
            } else {
                onChangeIfDifferentValue({
                    type
                });
            }
        },
        onClose: (opts)=>{
            if (opts === null || opts === void 0 ? void 0 : opts.revert) {
                setType(value.type);
                setFromDate(getInitialFromDate());
                setToDate(getInitialToDate());
            }
        },
        children: [
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'last_billing_date_all'
                },
                label: agreementFilterTexts.filters.dateRange.selectAll(),
                testId: LastBillingDateFilterTID.All(),
                checked: type === "none",
                onChange: ()=>setType("none")
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'last_billing_date_ever'
                },
                label: getLastBillingDateOptionLabel("ever", agreementFilterTexts.filters.lastBillingDateOptions),
                testId: LastBillingDateFilterTID.Ever(),
                checked: type === "ever",
                onChange: ()=>setType("ever")
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'last_billing_date_never'
                },
                label: getLastBillingDateOptionLabel("never", agreementFilterTexts.filters.lastBillingDateOptions),
                testId: LastBillingDateFilterTID.Never(),
                checked: type === "never",
                onChange: ()=>setType("never")
            }),
            /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                bi: {
                    name: 'last_billing_date_range'
                },
                label: getLastBillingDateOptionLabel("range", agreementFilterTexts.filters.lastBillingDateOptions),
                testId: LastBillingDateFilterTID.Range(),
                checked: type === "range",
                onChange: ()=>{
                    setType("range");
                    const now = startOfDay(getNow());
                    if (!fromDate) {
                        setFromDate(now);
                    }
                    if (!toDate) {
                        setToDate(now);
                    }
                }
            }),
            type === "range" && fromDate && toDate && /*#__PURE__*/ _jsx(DateRangePicker, {
                testId: LastBillingDateFilterTID.DatePicker(),
                fromDate: fromDate,
                toDate: toDate,
                onChange: (data)=>{
                    setFromDate(data.fromDate);
                    setToDate(data.toDate);
                },
                onInputFocus: onInputFocus,
                onInputBlur: onInputBlur
            })
        ]
    });
}
export var PaymentMethodFilterValue = /*#__PURE__*/ function(PaymentMethodFilterValue) {
    PaymentMethodFilterValue["CreditCard"] = "credit";
    PaymentMethodFilterValue["BankAccount"] = "bank";
    PaymentMethodFilterValue["VerificationInitiated"] = "verificationInitiated";
    PaymentMethodFilterValue["VerificationFailed"] = "verificationFailed";
    PaymentMethodFilterValue["NotSet"] = "notSet";
    return PaymentMethodFilterValue;
}({});
function getPaymentMethodFilterOptions(labels) {
    return [
        {
            value: "credit",
            label: labels.card()
        },
        {
            value: "bank",
            label: labels.bankAccount()
        },
        {
            value: "verificationInitiated",
            label: labels.manualVerificationInitiated()
        },
        {
            value: "verificationFailed",
            label: labels.manualVerificationFailed()
        },
        {
            value: "notSet",
            label: labels.notSet()
        }
    ];
}
export var CompletionFilterValue = /*#__PURE__*/ function(CompletionFilterValue) {
    CompletionFilterValue["Completed"] = "completed";
    CompletionFilterValue["NotCompleted"] = "notCompleted";
    return CompletionFilterValue;
}({});
function getCompletionFilterOptions(labels) {
    return [
        {
            value: "notCompleted",
            label: labels.notCompleted()
        },
        {
            value: "completed",
            label: labels.completed()
        }
    ];
}
export var HasCreditFilterValue = /*#__PURE__*/ function(HasCreditFilterValue) {
    HasCreditFilterValue["Yes"] = "yes";
    HasCreditFilterValue["No"] = "no";
    return HasCreditFilterValue;
}({});
function getHasCreditFilterOptions(labels) {
    let aiVariant = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
    return [
        {
            value: "yes",
            label: aiVariant ? labels.hasCredit() : labels.yes()
        },
        {
            value: "no",
            label: aiVariant ? labels.none() : labels.no()
        }
    ];
}
export var AutomaticFilterValue = /*#__PURE__*/ function(AutomaticFilterValue) {
    AutomaticFilterValue["Active"] = "active";
    AutomaticFilterValue["Paused"] = "paused";
    return AutomaticFilterValue;
}({});
function getAutomaticFilterOptions(labels) {
    return [
        {
            value: "active",
            label: labels.active()
        },
        {
            value: "paused",
            label: labels.paused()
        }
    ];
}
export var BillingTriggerFilterValue = /*#__PURE__*/ function(BillingTriggerFilterValue) {
    BillingTriggerFilterValue["Automatic"] = "automatic";
    BillingTriggerFilterValue["Manual"] = "manual";
    return BillingTriggerFilterValue;
}({});
function getBillingTriggerFilterOptions(labels) {
    return [
        {
            value: "automatic",
            label: labels.automatic()
        },
        {
            value: "manual",
            label: labels.manual()
        }
    ];
}
function DateRangePicker(props) {
    const { fromDate, toDate, testId, onChange, onInputBlur, onInputFocus } = props;
    const { t: agreementFilterTexts } = useLocaleKeys(agreementFilterTextsPath);
    const { t: dateRangePickerTexts } = useLocaleKeys(dateRangePickerTextsPath);
    const toFromErrorMessage = dateRangePickerTexts.toFromErrorMessage();
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(DatePicker, {
                bi: {
                    name: 'agreement_date_from'
                },
                label: agreementFilterTexts.filters.dateRange.from(),
                testId: testId && "".concat(testId, "-from"),
                value: fromDate,
                maxDate: toDate,
                maxDateMessage: toFromErrorMessage,
                onChange: (date)=>date && onChange && onChange({
                        fromDate: date,
                        toDate
                    }),
                onFocus: onInputFocus,
                onBlur: onInputBlur,
                mobileVariant: "input"
            }),
            /*#__PURE__*/ _jsx(DatePicker, {
                bi: {
                    name: 'agreement_date_to'
                },
                label: agreementFilterTexts.filters.dateRange.to(),
                testId: testId && "".concat(testId, "-to"),
                value: toDate,
                minDate: fromDate,
                minDateMessage: toFromErrorMessage,
                onChange: (date)=>date && onChange && onChange({
                        fromDate,
                        toDate: date
                    }),
                onFocus: onInputFocus,
                onBlur: onInputBlur,
                mobileVariant: "input"
            })
        ]
    });
}
function getPriceIncreaseFilterOptions(labels) {
    return [
        {
            value: "automatic",
            label: labels.included()
        },
        {
            value: "none",
            label: labels.notIncluded()
        }
    ];
}
function getMultiSelectLabelsByFilterType(agreementFilterTexts, recurrencePeriodsTexts, isVendor) {
    return {
        ["agreementStatus"]: keyBy(getAgreementStatusFilterOptions(agreementFilterTexts.filters.statusOptions), 'value'),
        ["paymentMethod"]: keyBy(getPaymentMethodFilterOptions(agreementFilterTexts.filters.paymentMethodOptions), 'value'),
        ["hasCredit"]: keyBy(getHasCreditFilterOptions(agreementFilterTexts.filters.hasCreditOptions), 'value'),
        ["completion"]: keyBy(getCompletionFilterOptions(agreementFilterTexts.filters.completionOptions), 'value'),
        ["recurrence"]: keyBy(getRecurrenceFilterOptions(recurrencePeriodsTexts), 'value'),
        ["automatic"]: keyBy(getAutomaticFilterOptions(agreementFilterTexts.filters.automaticOptions), 'value'),
        ["billingTrigger"]: keyBy(getBillingTriggerFilterOptions(agreementFilterTexts.filters.billingTriggerOptions), 'value'),
        ["servicePriceIncreaseState"]: keyBy(getPriceIncreaseFilterOptions(agreementFilterTexts.filters.priceIncreaseOptions), 'value'),
        ["hasAmendments"]: keyBy(getHasAmendmentsFilterOptions(agreementFilterTexts.filters.hasAmendmentsOptions), 'value'),
        ["amendmentsStatus"]: keyBy(getAmendmentsStatusFilterOptions(agreementFilterTexts.filters.amendmentsStatusOptions, isVendor), 'value')
    };
}
function formatMultiSelectDescription(values, labelByValue) {
    if (values.length === 0) return undefined;
    if (!labelByValue) return values.join(', ');
    const labels = values.map((v)=>{
        var _ref;
        var _labelByValue_v;
        return (_ref = (_labelByValue_v = labelByValue[v]) === null || _labelByValue_v === void 0 ? void 0 : _labelByValue_v.label) !== null && _ref !== void 0 ? _ref : v;
    });
    return labels.join(', ');
}
export function formatFilterDescription(params) {
    const { filterType, value, agreementFilterTexts, recurrencePeriodsTexts, isVendor } = params;
    switch(filterType){
        case "effectiveDate":
            {
                const typed = value;
                if (!typed.fromDate || !typed.toDate) return undefined;
                return "".concat(timeModel.formatDate(typed.fromDate), " - ").concat(timeModel.formatDate(typed.toDate));
            }
        case "approvalDate":
            {
                const typed = value;
                if (!hasApprovalDateRange(typed)) return undefined;
                return compact(typed.dateRange).join(' - ');
            }
        case "lastBillingDate":
            {
                const typed = value;
                switch(typed.type){
                    case "ever":
                        return getLastBillingDateOptionLabel("ever", agreementFilterTexts.filters.lastBillingDateOptions);
                    case "never":
                        return getLastBillingDateOptionLabel("never", agreementFilterTexts.filters.lastBillingDateOptions);
                    case "range":
                        return "".concat(timeModel.formatDate(typed.fromDate), " - ").concat(timeModel.formatDate(typed.toDate));
                    case "none":
                        return undefined;
                }
                break;
            }
        case "serviceNames":
            return formatMultiSelectDescription(value);
        default:
            {
                const labelsByType = getMultiSelectLabelsByFilterType(agreementFilterTexts, recurrencePeriodsTexts, isVendor);
                return formatMultiSelectDescription(value, labelsByType[filterType]);
            }
    }
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSimpleMultiSelectFilterConfig(type) {
    return {
        hasValue (v) {
            return v.length > 0;
        },
        getEmptyValue () {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return [];
        },
        getRequestParam (v) {
            return {
                [type]: v.length ? v.join(',') : undefined
            };
        }
    };
}
const effectiveDateConfig = {
    hasValue (effectiveDate) {
        return !!(effectiveDate.fromDate && effectiveDate.toDate);
    },
    getEmptyValue () {
        return {};
    },
    getRequestParam (effectiveDate) {
        return {
            fromDate: effectiveDate.fromDate && timeModel.toDatestampString(effectiveDate.fromDate),
            toDate: effectiveDate.toDate && timeModel.toDatestampString(effectiveDate.toDate)
        };
    },
    serialize (param) {
        let { fromDate, toDate } = param;
        return fromDate && toDate ? "".concat(timeModel.toDatestampString(fromDate), ",").concat(timeModel.toDatestampString(toDate)) : '';
    },
    deserialize (str) {
        const splitted = str.split(',');
        return splitted.length === 2 ? {
            fromDate: timeModel.fromDatestampString(splitted[0]),
            toDate: timeModel.fromDatestampString(splitted[1])
        } : {};
    }
};
const DATESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
function isDateRangeOption(value) {
    return Object.values(DateRange).includes(value);
}
// WHY: HasApprovedAtDateRange keys off approvedAtFrom, so a range with no start filters nothing.
function hasApprovalDateRange(approvalDate) {
    if (approvalDate.dateRangeOption === DateRange.AllTime) return false;
    return !!approvalDate.dateRange[0];
}
export function toApprovalDatestamp(displayDate) {
    if (!displayDate) return undefined;
    const parsed = new Date(displayDate);
    return Number.isNaN(parsed.getTime()) ? undefined : timeModel.toDatestampString(parsed);
}
export function toApprovalDisplayDate(datestamp) {
    if (!DATESTAMP_PATTERN.test(datestamp)) return undefined;
    const parsed = timeModel.fromDatestampString(datestamp);
    // WHY: the pattern only checks digit shape, so 9999-99-99 would roll over into a real date.
    if (timeModel.toDatestampString(parsed) !== datestamp) return undefined;
    return formatNumericUSDate(parsed);
}
function emptyApprovalDate() {
    return {
        dateRangeOption: DateRange.AllTime,
        dateRange: []
    };
}
const approvalDateConfig = {
    hasValue: hasApprovalDateRange,
    getEmptyValue: emptyApprovalDate,
    getRequestParam (approvalDate) {
        if (!hasApprovalDateRange(approvalDate)) return {
            approvedAtFrom: undefined,
            approvedAtTo: undefined
        };
        return {
            approvedAtFrom: toApprovalDatestamp(approvalDate.dateRange[0]),
            approvedAtTo: toApprovalDatestamp(approvalDate.dateRange[1])
        };
    },
    serialize (approvalDate) {
        var _toApprovalDatestamp;
        const from = hasApprovalDateRange(approvalDate) ? toApprovalDatestamp(approvalDate.dateRange[0]) : undefined;
        if (from == null) return '';
        return "".concat(approvalDate.dateRangeOption, ",").concat(from, ",").concat((_toApprovalDatestamp = toApprovalDatestamp(approvalDate.dateRange[1])) !== null && _toApprovalDatestamp !== void 0 ? _toApprovalDatestamp : '');
    },
    deserialize (str) {
        const parts = str.split(',');
        if (parts.length !== 3) return emptyApprovalDate();
        const [option, from, to] = parts;
        if (!isDateRangeOption(option)) return emptyApprovalDate();
        const dateRange = [
            toApprovalDisplayDate(from),
            toApprovalDisplayDate(to)
        ];
        if (dateRange[0] == null && dateRange[1] == null) return emptyApprovalDate();
        return {
            dateRangeOption: option,
            dateRange
        };
    }
};
const lastBillingDateConfig = {
    hasValue (value) {
        return value.type !== "none";
    },
    getEmptyValue () {
        return {
            type: "none"
        };
    },
    getRequestParam (lastBillingDate) {
        const isLastBilledWithinRange = lastBillingDate.type === "range";
        return {
            lastBilled: lastBillingDate.type !== "none" ? lastBillingDate.type : undefined,
            lastBilledFrom: isLastBilledWithinRange ? timeModel.toDatestampString(lastBillingDate.fromDate) : undefined,
            lastBilledTo: isLastBilledWithinRange ? timeModel.toDatestampString(lastBillingDate.toDate) : undefined
        };
    },
    serialize (data) {
        if (data.type === "none") {
            return '';
        }
        if (data.type === "range") {
            return "".concat(timeModel.toDatestampString(data.fromDate), ",").concat(timeModel.toDatestampString(data.toDate));
        }
        return data.type;
    },
    deserialize (lastBilled) {
        if (!lastBilled) {
            return {
                type: "none"
            };
        }
        if (lastBilled === "ever" || lastBilled === "never") {
            return {
                type: lastBilled
            };
        }
        const dates = lastBilled.split(',');
        if (dates.length !== 2) {
            return {
                type: "none"
            };
        }
        const fromDate = timeModel.fromDatestampString(dates[0]);
        const toDate = timeModel.fromDatestampString(dates[1]);
        return {
            type: "range",
            fromDate,
            toDate
        };
    }
};
export const filtersConfig = {
    ["effectiveDate"]: effectiveDateConfig,
    ["approvalDate"]: approvalDateConfig,
    ["lastBillingDate"]: lastBillingDateConfig,
    ["agreementStatus"]: getSimpleMultiSelectFilterConfig("agreementStatus"),
    ["paymentMethod"]: getSimpleMultiSelectFilterConfig("paymentMethod"),
    ["completion"]: getSimpleMultiSelectFilterConfig("completion"),
    ["recurrence"]: getSimpleMultiSelectFilterConfig("recurrence"),
    ["hasCredit"]: getSimpleMultiSelectFilterConfig("hasCredit"),
    ["automatic"]: getSimpleMultiSelectFilterConfig("automatic"),
    ["billingTrigger"]: getSimpleMultiSelectFilterConfig("billingTrigger"),
    ["serviceNames"]: getSimpleMultiSelectFilterConfig("serviceNames"),
    ["servicePriceIncreaseState"]: getSimpleMultiSelectFilterConfig("servicePriceIncreaseState"),
    ["hasAmendments"]: getSimpleMultiSelectFilterConfig("hasAmendments"),
    ["amendmentsStatus"]: getSimpleMultiSelectFilterConfig("amendmentsStatus")
};
export function serializeFilter(type, value) {
    const { serialize } = filtersConfig[type];
    return serialize ? serialize(value) : value;
}
export function makeTableFilterConfig(params) {
    const { serviceNameOptions, agreementFilterTexts, recurrencePeriodsTexts, isVendor, now, aiVariant = false } = params;
    return {
        ["agreementStatus"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getAgreementStatusFilterOptions(agreementFilterTexts.filters.statusOptions)
        },
        ["effectiveDate"]: {
            type: SearchFilterType.CustomFilter,
            removable: true,
            component: EffectiveDateFilter,
            initialValue: {}
        },
        ["approvalDate"]: {
            type: SearchFilterType.DateRangeWithPresets,
            removable: true,
            customDateLimits: {
                max: now
            }
        },
        ["lastBillingDate"]: {
            type: SearchFilterType.CustomFilter,
            removable: true,
            component: LastBillingDateFilter,
            initialValue: {
                type: "none"
            }
        },
        ["paymentMethod"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getPaymentMethodFilterOptions(agreementFilterTexts.filters.paymentMethodOptions)
        },
        ["hasCredit"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getHasCreditFilterOptions(agreementFilterTexts.filters.hasCreditOptions, aiVariant)
        },
        ["completion"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getCompletionFilterOptions(agreementFilterTexts.filters.completionOptions)
        },
        ["recurrence"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getRecurrenceFilterOptions(recurrencePeriodsTexts)
        },
        ["automatic"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getAutomaticFilterOptions(agreementFilterTexts.filters.automaticOptions)
        },
        ["billingTrigger"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getBillingTriggerFilterOptions(agreementFilterTexts.filters.billingTriggerOptions)
        },
        ["serviceNames"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            withSearchbox: true,
            ellipsis: true,
            options: serviceNameOptions
        },
        ["servicePriceIncreaseState"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getPriceIncreaseFilterOptions(agreementFilterTexts.filters.priceIncreaseOptions)
        },
        ["hasAmendments"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getHasAmendmentsFilterOptions(agreementFilterTexts.filters.hasAmendmentsOptions)
        },
        ["amendmentsStatus"]: {
            type: SearchFilterType.MultiSelectFilter,
            removable: true,
            options: getAmendmentsStatusFilterOptions(agreementFilterTexts.filters.amendmentsStatusOptions, isVendor)
        }
    };
}
